import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Slide,
  Stack,
  Chip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import {
  subscribeAiReviewNotifications,
  dismissAiReview,
  getNotificationRedirectPath,
} from '../../services/fcmNotificationService';

/**
 * High-visibility in-app notification banner for doctors when AI notes are staged for review.
 */
const AiNotificationBanner = () => {
  const [reviews, setReviews] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = subscribeAiReviewNotifications((list) => {
      setReviews(list || []);
    });
    return () => unsubscribe();
  }, []);

  // If there's an active review staged by the backend
  if (reviews && reviews.length > 0) {
    const activeReview = reviews[0];

    const handleReviewClick = () => {
      const route = getNotificationRedirectPath(activeReview) || activeReview.route;
      dismissAiReview(activeReview.consultationId);
      navigate(route);
    };

    const handleDismiss = () => {
      dismissAiReview(activeReview.consultationId);
    };

    return (
      <Slide direction="down" in={Boolean(activeReview)} mountOnEnter unmountOnExit>
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            top: 76,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1400,
            width: '92%',
            maxWidth: 720,
            p: 2,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #1A1F36 0%, #0F1424 100%)',
            color: '#FFFFFF',
            border: '1px solid rgba(138, 75, 255, 0.4)',
            boxShadow: '0 12px 32px rgba(15, 20, 36, 0.45), 0 0 16px rgba(138, 75, 255, 0.25)',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #8A4BFF 0%, #3B82F6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 0 12px rgba(138, 75, 255, 0.6)',
                }}
              >
                <AutoAwesomeIcon sx={{ color: '#FFF', fontSize: 24 }} />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={0.25}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#FFFFFF', fontSize: 15 }}>
                    {activeReview.title || 'AI-extracted notes ready to review'}
                  </Typography>
                  <Chip
                    label="Ready"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(52, 211, 153, 0.2)',
                      color: '#34D399',
                      fontSize: 10,
                      fontWeight: 700,
                      height: 20,
                    }}
                  />
                </Stack>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: 13,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {activeReview.body || `Review and confirm notes from ${activeReview.patientName || 'the'}'s consultation`}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, alignSelf: { xs: 'flex-end', sm: 'center' } }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleReviewClick}
                endIcon={<ArrowForwardIcon />}
                data-testid="banner-review-ai-cards"
                sx={{
                  bgcolor: '#8A4BFF',
                  color: '#FFF',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 2,
                  borderRadius: 2,
                  '&:hover': {
                    bgcolor: '#7434F5',
                  },
                }}
              >
                Review Notes
              </Button>
              <IconButton
                size="small"
                onClick={handleDismiss}
                aria-label="Dismiss AI cards review notification"
                sx={{ color: 'rgba(255, 255, 255, 0.6)', '&:hover': { color: '#FFF' } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </Paper>
      </Slide>
    );
  }

  return null;
};

export default AiNotificationBanner;
